import { lazy, Suspense, useCallback, useMemo, useState } from "react";
import { useQueuedPopup } from "../../../hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import {
  usePopupTimings,
  useIsHomeDisplayReady,
} from "@/modules/homeDisplay/hooks";
import type {
  CustomHomePopupItem,
  CustomHomePopupResult,
} from "./useCustomHomePopup.types";
import { useUserDataFlattened } from "@/modules";

const CustomHomePopup = lazy(() =>
  import("../../CustomHomePopup/CustomHomePopup").then(m => ({
    default: m.CustomHomePopup,
  }))
);

const CUSTOM_HOME_POPUP_CONFIG = POPUP_CONFIGS.CUSTOM_HOME_POPUP;

export interface CustomHomePopupContentProps {
  items: CustomHomePopupItem[];
  onComplete: () => void;
}

// 提到 hook 外部：避免每次 render 都创建新组件类型 / 新函数，
// 否则 useQueuedPopup 的 addToQueue 依赖随之变化，会在暂停→恢复的竞态窗口内重复入队，
// 导致关闭注册弹窗后自定义弹窗弹两次（详见 BUG-A）。
function CustomHomePopupContent({
  items,
  onComplete,
}: CustomHomePopupContentProps) {
  const [visible, setVisible] = useState(true);

  const handleClose = () => {
    setVisible(false);
    onComplete();
  };

  return (
    <Suspense fallback={null}>
      <CustomHomePopup visible={visible} items={items} onClose={handleClose} />
    </Suspense>
  );
}

export const useCustomHomePopup = (): CustomHomePopupResult => {
  const popupTimings = usePopupTimings();
  const { data: userInfo, isAuthenticated } = useUserDataFlattened();
  const isConfigReady = useIsHomeDisplayReady();

  // 获取进入首页的自定义弹窗数据
  const customItems = useMemo(() => {
    if (!popupTimings) return [];

    const enterHomePopup = popupTimings.find(
      timing => timing.timingType === "ENTER_HOME_PAGE"
    );

    if (!enterHomePopup || !enterHomePopup.enabled) return [];

    const items = (enterHomePopup.customItems || [])
      .filter(item => item.enabled)
      .sort((a, b) => a.sort - b.sort);
    return items as CustomHomePopupItem[];
  }, [popupTimings]);

  const hasItems = customItems.length > 0;

  // 优先级取自弹窗配置的固定值
  const priority = CUSTOM_HOME_POPUP_CONFIG.priority;

  // 稳定 shouldShow / renderContent 引用：仅在真正依赖变化时才变，
  // 避免驱动 useQueuedPopup 的自动入队 effect 反复重跑（BUG-A 重复入队根因）。
  const shouldShow = useCallback(
    () => isConfigReady && hasItems,
    [isConfigReady, hasItems]
  );

  const renderContent = useCallback(
    (onComplete: () => void) => (
      <CustomHomePopupContent items={customItems} onComplete={onComplete} />
    ),
    [customItems]
  );

  const { trigger } = useQueuedPopup({
    popupId: CUSTOM_HOME_POPUP_CONFIG.id,
    priority,
    shouldShow,
    renderContent,
    showOnce: true,
    allowManualTrigger: false,
    autoTrigger: true,
    dependencies: [isConfigReady, customItems.length, isAuthenticated],
    userBinding: true,
    getUserId: () => userInfo?.userId,
  });

  return {
    items: customItems,
    hasItems,
    trigger,
  };
};
